import React, { useEffect } from "react";
import styled from "styled-components";

import { Nav } from "../components/Nav";
import Footer from "../components/Footer";
import { Icon } from "../components/Icon";
import { TOC, DADOS, PRAZOS, DIREITOS } from "../data/privacidade";
import {
    SITE_URL,
    setMetaTag,
    setLinkTag,
    restoreDefaultMeta
} from "../utils/pageMeta";

const PRIVACY_URL = `${SITE_URL}/privacidade`;
const PRIVACY_TITLE = "Política de privacidade · Mango Software House";
const PRIVACY_DESCRIPTION = "Quais dados a Mango Software House coleta neste site, para que os usa, por quanto tempo os guarda e como pedir para apagá-los, sob a LGPD.";

const Page = styled.main`
    width: 100%;
    padding: 148px 0 0;
    overflow-x: hidden;

    @media only screen and (max-width: 720px) {
        padding-top: 118px;
    }
`;

const Section = styled.section`
    width: min(760px, calc(100vw - 32px));
    margin: 0 auto;

    @media only screen and (max-width: 640px) {
        width: min(100%, calc(100vw - 24px));
    }
`;

const Hero = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
`;

const Eyebrow = styled.span`
    font-size: 12.5px;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--brand);
`;

const Title = styled.h1`
    margin: 0;
    font-size: clamp(2rem, 4vw, 2.7rem);
    font-weight: 800;
    line-height: 1.06;
    letter-spacing: -0.03em;
    color: var(--ink);
`;

const Lead = styled.p`
    margin: 0;
    font-size: 1.1rem;
    font-weight: 500;
    line-height: 1.65;
    color: var(--muted);
`;

const MetaStrip = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    padding: 16px 18px;
    border-radius: 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
`;

const MetaCol = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const MetaLabel = styled.span`
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
`;

const MetaValue = styled.strong`
    font-size: 15.5px;
    font-weight: 800;
    color: var(--ink);
`;

const MetaDivider = styled.span`
    width: 1px;
    height: 32px;
    background: var(--line);
`;

const TocLabel = styled.span`
    display: block;
    margin: 24px 0 10px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--muted);
`;

const TocGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid var(--line);

    @media only screen and (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const TocLink = styled.a`
    display: flex;
    align-items: baseline;
    gap: 9px;
    padding: 7px 0;
    font-size: 14.5px;
    font-weight: 600;
    color: var(--brand-2);

    span { font-size: 12.5px; font-weight: 800; color: var(--muted-soft); }
    &:hover { color: var(--ink); }
`;

const DocSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 34px 0;
    border-top: 1px solid #f0ecf8;
`;

const H2 = styled.h2`
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: -0.015em;
    color: var(--ink);
`;

const P = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.7;
    color: var(--muted);

    strong { color: var(--ink); font-weight: 800; }
    a { color: var(--brand); font-weight: 700; }
`;

const CanalBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 18px 20px;
    border-radius: 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
`;

const CanalLabel = styled.span`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
`;

const CanalEmail = styled.a`
    font-size: 16px;
    font-weight: 800;
    color: var(--brand);
    overflow-wrap: anywhere;
`;

const CanalNote = styled.span`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.55;
    color: var(--muted);
`;

const TableWrap = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 15px;
`;

const Th = styled.th`
    text-align: left;
    padding: 10px 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
`;

const Td = styled.td`
    padding: 12px;
    border: 1px solid var(--line);
    vertical-align: top;
    font-size: 14.5px;
    line-height: 1.5;
    color: var(--muted);

    &:first-child { font-weight: 800; color: var(--ink); }
`;

const NaoFazemos = styled.p`
    margin: 0;
    font-size: 15.5px;
    font-weight: 600;
    line-height: 1.65;
    color: var(--success-strong);
    padding: 16px 18px;
    border-radius: 12px;
    background: var(--success-bg);
    border: 1px solid var(--success-line);

    strong { font-weight: 800; }
`;

const PrazosGrid = styled.div`
    display: grid;
    gap: 10px;
`;

const PrazoRow = styled.div`
    display: grid;
    grid-template-columns: 170px minmax(0, 1fr);
    gap: 16px;
    padding: 16px 18px;
    border-radius: 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);
    align-items: baseline;

    @media only screen and (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const PrazoValue = styled.strong`
    font-size: 15.5px;
    font-weight: 800;
    color: var(--ink);
`;

const PrazoText = styled.span`
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;
    color: var(--muted);
`;

const DireitosGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    @media only screen and (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const DireitoRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid var(--line);

    .material-symbols-rounded { color: var(--success); margin-top: 2px; }
`;

const DireitoTitle = styled.strong`
    display: block;
    font-size: 14.5px;
    font-weight: 800;
    color: var(--ink);
`;

const DireitoDesc = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.5;
    color: var(--muted);
`;

const ContatoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    @media only screen and (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const ContatoCard = styled.a`
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 18px 20px;
    border-radius: 12px;
    background: var(--bg-soft);
    border: 1px solid var(--line);

    .material-symbols-rounded { color: var(--brand); font-size: 23px; }
    &:hover { border-color: #c9bdea; }
`;

const ContatoName = styled.strong`
    display: block;
    font-size: 15px;
    font-weight: 800;
    color: var(--ink);
    overflow-wrap: anywhere;
`;

const ContatoNote = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--muted);
`;

function PrivacyPolicy() {
    useEffect(() => {
        document.title = PRIVACY_TITLE;
        setMetaTag('meta[name="description"]', { name: "description", content: PRIVACY_DESCRIPTION });
        setMetaTag('meta[name="robots"]', { name: "robots", content: "index, follow" });
        setMetaTag('meta[property="og:title"]', { property: "og:title", content: PRIVACY_TITLE });
        setMetaTag('meta[property="og:description"]', { property: "og:description", content: PRIVACY_DESCRIPTION });
        setMetaTag('meta[property="og:url"]', { property: "og:url", content: PRIVACY_URL });
        setLinkTag('link[rel="canonical"]', { rel: "canonical", href: PRIVACY_URL });

        return () => {
            restoreDefaultMeta();
        };
    }, []);

    return (
        <>
            <Nav />

            <Page id="top">
                <Section>
                    <Hero>
                        <Eyebrow>Documento</Eyebrow>
                        <Title>Política de privacidade</Title>
                        <Lead>Esta página explica quais dados a Mango Software House coleta neste site, para que os usa, por quanto tempo os guarda e como você pede para apagá-los. Está escrita para ser lida por quem preencheu o formulário, não por advogado.</Lead>
                        <MetaStrip>
                            <MetaCol><MetaLabel>Em vigor desde</MetaLabel><MetaValue>26 de julho de 2026</MetaValue></MetaCol>
                            <MetaDivider />
                            <MetaCol><MetaLabel>Lei aplicável</MetaLabel><MetaValue>Lei 13.709/2018 (LGPD)</MetaValue></MetaCol>
                            <MetaDivider />
                            <MetaCol><MetaLabel>Resposta a pedidos</MetaLabel><MetaValue>até 15 dias</MetaValue></MetaCol>
                        </MetaStrip>
                    </Hero>

                    <TocLabel>Nesta página</TocLabel>
                    <TocGrid>
                        {TOC.map((item) => (
                            <TocLink key={item.n} href={item.href}><span>{item.n}</span>{item.label}</TocLink>
                        ))}
                    </TocGrid>

                    <DocSection id="s1">
                        <H2>1. Quem trata os seus dados</H2>
                        <P>Os dados enviados por este site são tratados pela <strong>Mango Software House</strong>, inscrita no CNPJ 33.545.792/0001-39, empresa brasileira. Somos a controladora dos dados: quem decide para que eles servem e por quanto tempo ficam guardados.</P>
                        <CanalBox>
                            <CanalLabel>Canal para assuntos de privacidade</CanalLabel>
                            <CanalEmail href="mailto:contato@mangosoftwarehouse.com">contato@mangosoftwarehouse.com</CanalEmail>
                            <CanalNote>É o mesmo endereço do contato comercial. Escreva “privacidade” no assunto e o pedido é encaminhado para quem responde por isso.</CanalNote>
                        </CanalBox>
                        <P>Pedidos sobre privacidade são respondidos em até <strong>15 dias</strong>, contados do recebimento da mensagem.</P>
                    </DocSection>

                    <DocSection id="s2">
                        <H2>2. Que dados coletamos, e para quê</H2>
                        <P>Só o necessário para responder ao seu pedido. Não pedimos CPF, endereço, dados bancários nem qualquer informação sensível no formulário do site.</P>
                        <TableWrap>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Dado</Th>
                                        <Th>Para que serve</Th>
                                        <Th>Base legal</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {DADOS.map((linha) => (
                                        <tr key={linha.dado}>
                                            <Td>{linha.dado}</Td>
                                            <Td>{linha.uso}</Td>
                                            <Td>{linha.base}</Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TableWrap>
                        <NaoFazemos>O que nós <strong>não</strong> fazemos com esses dados: não vendemos, não trocamos, não usamos para lista de e-mail marketing e não enviamos mensagem promocional a quem só pediu um diagnóstico.</NaoFazemos>
                    </DocSection>

                    <DocSection id="s3">
                        <H2>3. Com quem os dados são compartilhados</H2>
                        <P>Os dados ficam com o time da Mango. Passam por prestadores de serviço apenas na medida em que o site e o e-mail funcionam através deles — por exemplo, o serviço que hospeda o site e o serviço de e-mail que recebe a mensagem do formulário. Esses prestadores atuam como operadores: podem processar, não podem usar para outra finalidade.</P>
                        <P>Se o contato começar pelo WhatsApp, a conversa também passa pela plataforma do WhatsApp, que tem política própria. Fora disso, não há compartilhamento com terceiros — nem com parceiros comerciais, nem para publicidade.</P>
                        <P>Os dados enviados pelo formulário são registrados no <strong>CRM da Mango</strong>, usado para acompanhar o atendimento até a resposta e o eventual orçamento. O site também usa uma ferramenta de medição de acesso, descrita na seção 5. Todos esses serviços atuam como operadores: podem processar, não podem usar para outra finalidade.</P>
                    </DocSection>

                    <DocSection id="s4">
                        <H2>4. Por quanto tempo guardamos</H2>
                        <PrazosGrid>
                            {PRAZOS.map((item) => (
                                <PrazoRow key={item.prazo}>
                                    <PrazoValue>{item.prazo}</PrazoValue>
                                    <PrazoText>{item.oQue}</PrazoText>
                                </PrazoRow>
                            ))}
                        </PrazosGrid>
                        <P>Você pode pedir a exclusão antes desses prazos a qualquer momento, e nós apagamos — exceto o que a lei fiscal obriga a manter quando já houve contrato assinado.</P>
                    </DocSection>

                    <DocSection id="s5">
                        <H2>5. Cookies e medição de acesso</H2>
                        <P>Este site não usa cookie para perfilar visitante nem para publicidade. Há medição de audiência: ela serve para saber quantas pessoas visitaram cada página e por qual caminho chegaram — nunca para identificar quem você é.</P>
                        <P>Se você preferir não ser contabilizado, o bloqueio de cookies do seu navegador resolve, e nada no site deixa de funcionar por causa disso.</P>
                    </DocSection>

                    <DocSection id="s6">
                        <H2>6. Seus direitos</H2>
                        <P>A LGPD te dá os direitos abaixo sobre os seus dados. Para exercer qualquer um deles, basta escrever para <a href="mailto:contato@mangosoftwarehouse.com">contato@mangosoftwarehouse.com</a>. Respondemos em até 15 dias e não cobramos nada por isso.</P>
                        <DireitosGrid>
                            {DIREITOS.map((item) => (
                                <DireitoRow key={item.titulo}>
                                    <Icon name="check_circle" size="19px" />
                                    <span>
                                        <DireitoTitle>{item.titulo}</DireitoTitle>
                                        <DireitoDesc>{item.desc}</DireitoDesc>
                                    </span>
                                </DireitoRow>
                            ))}
                        </DireitosGrid>
                    </DocSection>

                    <DocSection id="s7">
                        <H2>7. Segurança</H2>
                        <P>O site trafega em HTTPS, o acesso às mensagens do formulário é restrito às pessoas do time que atendem o pedido, e as contas usadas para isso têm autenticação em dois fatores. Nenhum sistema é imune: se acontecer um incidente que possa te afetar, avisamos você e a Autoridade Nacional de Proteção de Dados, como a lei determina.</P>
                    </DocSection>

                    <DocSection id="s8">
                        <H2>8. Empresas em Portugal e fora do Brasil</H2>
                        <P>A Mango é uma empresa brasileira, emite nota fiscal brasileira e trata os dados sob a lei brasileira. Se a sua empresa está em Portugal ou em qualquer outro país, o contrato e o tratamento de dados seguem a legislação do Brasil — é bom saber disso antes de nos enviar informação da sua operação.</P>
                    </DocSection>

                    <DocSection id="s9">
                        <H2>9. Mudanças nesta política</H2>
                        <P>Se algo mudar — uma ferramenta nova, um prazo diferente — atualizamos esta página e trocamos a data de vigência no topo. Alteração que afete o uso dos seus dados de forma relevante é avisada por e-mail a quem já é cliente.</P>
                    </DocSection>

                    <DocSection id="s10" style={{ paddingBottom: "56px" }}>
                        <H2>10. Falar com a gente</H2>
                        <P>Dúvida sobre esta política, pedido de exclusão ou reclamação: escreva ou ligue. Se preferir tratar por escrito, o e-mail deixa registro para os dois lados.</P>
                        <ContatoGrid>
                            <ContatoCard href="mailto:contato@mangosoftwarehouse.com">
                                <Icon name="mail" size="23px" />
                                <span>
                                    <ContatoName>contato@mangosoftwarehouse.com</ContatoName>
                                    <ContatoNote>Resposta em até 15 dias</ContatoNote>
                                </span>
                            </ContatoCard>
                            <ContatoCard href="tel:+554191150056">
                                <Icon name="call" size="23px" />
                                <span>
                                    <ContatoName>+55 41 9115-0056</ContatoName>
                                    <ContatoNote>Horário comercial de Brasília</ContatoNote>
                                </span>
                            </ContatoCard>
                        </ContatoGrid>
                    </DocSection>
                </Section>
            </Page>

            <Footer />
        </>
    );
}

export { PrivacyPolicy };
export default PrivacyPolicy;
